/**
 * High level router.
 *
 * Note: It's recommended to compose related routes in internal router
 * components (e.g: `src/app/modules/Auth/pages/AuthPage`, `src/app/BasePage`).
 */

import {FC, useEffect, useState} from 'react'
import {Routes, Route, BrowserRouter, Navigate} from 'react-router-dom'
import {PrivateRoutes} from './PrivateRoutes'
import {ErrorsPage} from '../modules/errors/ErrorsPage'
import {Logout, AuthPage, useAuth} from '../modules/auth'
import {App} from '../App'
import {useAppDispatch, useAppSelector} from 'src/redux/storeHooks'
import {accessSessionTokenKeyName, loggedInUserDataKeyName, } from 'src/utils/constants'
import {setLoggingData} from 'src/redux/slices/authSlice'
import VerifyUser from '../modules/auth/components/VerifyUser'
import VerifyForgetPassword from '../modules/auth/components/VerifyForgetPassword'
import VerifyAccountActivation from '../modules/auth/components/VerifyAccountActivation'
import HandleOauth from '../pages/HandleOauth'
import HandleShopifyAppInstallation from '../pages/HandleShopifyAppInstallation'
import RefreshHandleOauth from '../pages/RefreshHandleOauth'
import HandleOauthUrl from '../pages/HandleOauthUrl'
import { userLoginUsingInstanceAsync } from 'src/redux/actions/authActions'
import CommerciumLoader from 'src/utils/CommerciumLoader'
import useAppToast from 'src/hooks/useAppToast'
import FullPageArticle from '../pages/dashboard/FullPageArticle'
import WhatsappIcon from 'src/utils/WhatsappIcon'
import LoginByURL from '../pages/LoginByURL'
import CommerciumLandingPage from '../pages/commerciumLanding/CommerciumLandingPage'
import { ExternalConnectionPage } from '../pages/ExternalConnectionPage';
import { AppOauthPage } from '../pages/AppOauthPage';
/**
 * Base URL of the website.
 *
 * @see https://facebook.github.io/create-react-app/docs/using-the-public-folder
 */
const {PUBLIC_URL} = process.env

const AppRoutes: FC = () => {
  const {currentUser} = useAuth()
  const [loading, setLoading] = useState(false)
  const {successToast, errorToast, warningToast} = useAppToast()
  const {isLoggedIn} = useAppSelector((state) => state.auth)
  const dispatch = useAppDispatch()
  useEffect(() => {
    
    const token = localStorage.getItem(accessSessionTokenKeyName)
    const loggedInUser = localStorage.getItem(loggedInUserDataKeyName)
    if (token) {
      dispatch(setLoggingData({isLoggedIn: true, userData: null}))
    }else{
      const urlParams = new URLSearchParams(window.location.search);
      const instance = urlParams.get('instance');
      console.log('base path to navigate', window.location.pathname );
      
      if(instance){
        setLoading(true)
        dispatch(userLoginUsingInstanceAsync({instance}))
        .then((response)=>{
          console.log('login using instance', response.payload);
          if(response.payload?.status==400){
            warningToast(response?.payload?.data?.message)
          }
          if(response.payload?.access){
            window.location.href = window.location.pathname
          }
          setLoading(false)
        })
        .catch((error)=>{
          console.error('Error login using instance:', error);
          setLoading(false)
        });
      }
    }
    return () => {
      dispatch(setLoggingData({isLoggedIn: false, userData: null}))
    }
  }, [])

  if(loading) return <CommerciumLoader />

  return (
    <BrowserRouter basename={PUBLIC_URL}>
      <Routes>
        <Route element={<App />}>
          <Route path='/bigcommerce-connection' element={<CommerciumLandingPage />} />
          <Route path='error/*' element={<ErrorsPage />} />
          <Route path='logout' element={<Logout />} />
          <Route path="/external-connection" element={<ExternalConnectionPage />} />
          <Route path='/app-oauth/:platform_name' element={<AppOauthPage />} />
          <Route path='/callback' element={<AppOauthPage />} />
          {isLoggedIn ? (
            <>
              <Route path='handle-app-installation/*' element={<HandleShopifyAppInstallation />} />
              <Route path='handle-oauth-callback-restricted/*' element={<HandleOauth />} />
              {/* temp solution for ecwid  */}
              <Route path='handle-oauth-callback/*' element={<HandleOauth />} />    
              {/* temp solution for ecwid  */}
              <Route path='/article/:articleId' element={<FullPageArticle />} />   
              <Route path='/*' element={<PrivateRoutes />} />
              {/* <Route index element={<Navigate to='/dashboard' />} /> */}
              {/*Redirect to Commercium from Disconnect */}
              <Route path='external-login' element={<LoginByURL />} />
            </>
          ) : (
            <>
              <Route path='handle-app-installation/*' element={<HandleShopifyAppInstallation />} />
              <Route path='handle-oauth-callback-restricted/*' element={<HandleOauth />} />
              <Route path='refresh-oauth-callback-restricted/*' element={<RefreshHandleOauth />} />

              {/*Redirect to Commercium from Disconnect */}
              <Route path='external-login' element={<LoginByURL />} />
              
              {/* for WIX type platforms  */}
              <Route path='handle-oauth-url/*' element={<HandleOauthUrl />} />              
              {/* for WIX type platforms  */}

              {/* temp solution for ecwid  */}
              <Route path='handle-oauth-callback/*' element={<HandleOauth />} />    
              {/* temp solution for ecwid  */}
              <Route path='verify-user-registration/:activation_token' element={<VerifyUser />} />
              <Route
                path='start-role-user-account-activation/:activation_token'
                element={<VerifyAccountActivation />}
              />
              <Route path='reset-password/:forgot_password_token' element={<VerifyForgetPassword />} />
              <Route path='auth/*' element={<AuthPage />} />
                <Route path='*' element={<Navigate to='/auth' />} />
            </>
          )}
        </Route>
      </Routes>
      {process.env['REACT_APP_ENV'] === 'production' && <WhatsappIcon />}
    </BrowserRouter>
  )
}

export {AppRoutes}
